import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import VideocamOffOutlinedIcon from "@mui/icons-material/VideocamOffOutlined";
import { alpha, useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";

/**
 * Confirma a política de câmera (e opções de equipe) antes de criar a sala.
 */
export default function StartVideoCallDialog({
  open,
  onClose,
  onConfirm,
  busy = false,
  context = "support",
  canSendInvite = false,
  activeRoomExists = false,
  reusedWarning = false,
  defaultPrivate = false,
  /** Em DM, privada = só os participantes da conversa (não “só eu”). */
  isDm = false,
}) {
  const { t } = useTranslation("chat");
  const theme = useTheme();
  const [requireCamera, setRequireCamera] = useState(false);
  const [isPrivate, setIsPrivate] = useState(Boolean(defaultPrivate));
  // null = usar padrão do workspace (env); só envia override quando o usuário altera o switch.
  const [recordingEnabled, setRecordingEnabled] = useState(null);
  const [recordingVisibility, setRecordingVisibility] = useState("space");
  const team = context === "team";
  const showReuseWarn = Boolean(activeRoomExists || reusedWarning);

  useEffect(() => {
    if (!open) return;
    setRequireCamera(false);
    setIsPrivate(Boolean(defaultPrivate));
    setRecordingEnabled(null);
    setRecordingVisibility("space");
  }, [open, defaultPrivate]);

  const options = [
    {
      value: false,
      title: t("video.cameraOptionalTitle", "Câmera opcional"),
      description: t(
        "video.cameraOptionalHint",
        "Podem entrar só com áudio e desligar o vídeo quando quiserem."
      ),
      icon: VideocamOffOutlinedIcon,
    },
    {
      value: true,
      title: t("video.cameraRequiredTitle", "Câmera obrigatória"),
      description: t(
        "video.cameraRequiredHint",
        "Só entra quem tiver câmera neste dispositivo. O vídeo não poderá ser desligado durante a chamada."
      ),
      icon: VideocamOutlinedIcon,
    },
  ];

  const confirmLabel = busy
    ? t("video.preparingInvite", "Preparando convite de vídeo…")
    : team
      ? t("video.startCallConfirmTeam", "Criar e entrar")
      : canSendInvite
        ? t("video.startCallConfirmSendEnter", "Criar, enviar e entrar")
        : t("video.startCallConfirmEnter", "Criar e entrar");

  const handleConfirm = () => {
    onConfirm?.({
      requireCamera,
      ...(recordingEnabled != null ? { recordingEnabled: Boolean(recordingEnabled) } : {}),
      ...(team
        ? {
            isPrivate,
            ...(recordingEnabled
              ? { recordingVisibility }
              : {}),
          }
        : {}),
    });
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        {t("video.startCallPolicyTitle", "Como será esta chamada?")}
      </DialogTitle>
      <DialogContent>
        {showReuseWarn ? (
          <Alert severity="warning" sx={{ mb: 2, py: 0.5 }}>
            <Typography variant="caption" sx={{ display: "block", lineHeight: 1.45 }}>
              {t(
                "video.reusedRoomWarning",
                "Já existe uma chamada ativa — o mesmo link será reenviado"
              )}
            </Typography>
          </Alert>
        ) : null}

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.5 }}>
          {team
            ? t(
                "video.startCallPolicyTeamBody",
                "Quem criar a sala define se a equipe pode desligar o vídeo. Isso fica visível para todos na hora de entrar."
              )
            : t(
                "video.startCallPolicyBody",
                "Você define se os participantes podem desligar o vídeo. Isso fica visível para todos na hora de entrar."
              )}
        </Typography>
        <Stack spacing={1}>
          {options.map((option) => {
            const selected = requireCamera === option.value;
            const Icon = option.icon;
            return (
              <Box
                key={String(option.value)}
                role="button"
                tabIndex={0}
                aria-pressed={selected}
                onClick={() => setRequireCamera(option.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    setRequireCamera(option.value);
                  }
                }}
                sx={{
                  px: 1.5,
                  py: 1.25,
                  borderRadius: 2,
                  border: 1,
                  borderColor: selected ? "primary.main" : "divider",
                  bgcolor: selected
                    ? alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.16 : 0.06)
                    : "transparent",
                  cursor: "pointer",
                }}
              >
                <Stack direction="row" spacing={1} alignItems="flex-start">
                  <Icon
                    sx={{
                      fontSize: 20,
                      mt: 0.15,
                      color: selected ? "primary.main" : "text.secondary",
                    }}
                  />
                  <Box>
                    <Typography variant="body2" fontWeight={800}>
                      {option.title}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", lineHeight: 1.4 }}>
                      {option.description}
                    </Typography>
                  </Box>
                </Stack>
              </Box>
            );
          })}
        </Stack>

        {team ? (
          <Stack spacing={1} sx={{ mt: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={isPrivate}
                  onChange={(e) => setIsPrivate(e.target.checked)}
                  size="small"
                />
              }
              label={
                <Box>
                  <Typography variant="body2" fontWeight={700}>
                    {t("video.privateRoomTitle", "Sala privada")}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {isDm
                      ? t(
                          "video.privateRoomHintDm",
                          "Só vocês dois desta conversa podem entrar. O link não funciona para outras pessoas."
                        )
                      : t(
                          "video.privateRoomHint",
                          "Só membros deste canal (ou quem já estiver na chamada) podem entrar."
                        )}
                  </Typography>
                </Box>
              }
              sx={{ alignItems: "flex-start", m: 0, ml: 0.5 }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={recordingEnabled !== false}
                  onChange={(e) => setRecordingEnabled(e.target.checked)}
                  size="small"
                />
              }
              label={
                <Box>
                  <Typography variant="body2" fontWeight={700}>
                    {t("video.recordCallTitle", "Gravar esta chamada")}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {recordingVisibility === "creator"
                      ? t(
                          "video.recordVisibilityCreatorHint",
                          "A gravação fica visível ao criador da chamada."
                        )
                      : t(
                          "video.recordVisibilitySpaceHint",
                          "A gravação fica visível a quem tem acesso a este espaço. Sem alteração, usa o padrão do workspace."
                        )}
                  </Typography>
                </Box>
              }
              sx={{ alignItems: "flex-start", m: 0, ml: 0.5 }}
            />
            {recordingEnabled !== false ? (
              <FormControl size="small" fullWidth sx={{ mt: 0.5 }}>
                <InputLabel id="video-recording-visibility-label">
                  {t("video.recordingVisibilityLabel", "Quem vê a gravação")}
                </InputLabel>
                <Select
                  labelId="video-recording-visibility-label"
                  label={t("video.recordingVisibilityLabel", "Quem vê a gravação")}
                  value={recordingVisibility}
                  onChange={(e) => setRecordingVisibility(e.target.value)}
                >
                  <MenuItem value="space">
                    {t("video.recordingVisibilitySpace", "Todo o espaço")}
                  </MenuItem>
                  <MenuItem value="creator">
                    {t("video.recordingVisibilityCreator", "Só o criador")}
                  </MenuItem>
                </Select>
              </FormControl>
            ) : null}
          </Stack>
        ) : (
          <FormControlLabel
            control={
              <Switch
                checked={recordingEnabled !== false}
                onChange={(e) => setRecordingEnabled(e.target.checked)}
                size="small"
              />
            }
            label={
              <Box>
                <Typography variant="body2" fontWeight={700}>
                  {t("video.recordCallTitle", "Gravar esta chamada")}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  {t(
                    "video.recordVisibilitySupportHint",
                    "Visível à equipe de atendimento desta solicitação. Sem alteração, usa o padrão do workspace."
                  )}
                </Typography>
              </Box>
            }
            sx={{ alignItems: "flex-start", m: 0, mt: 2, ml: 0.5 }}
          />
        )}

        <Alert severity={requireCamera ? "warning" : "info"} sx={{ mt: 2, py: 0.5 }}>
          <Typography variant="caption" sx={{ display: "block", lineHeight: 1.45 }}>
            {team
              ? t(
                  "video.startCallPolicyUseHintTeam",
                  "Use câmera obrigatória só quando o vídeo for necessário para a reunião — não para acompanhar a jornada. Não substitui chamadas com câmera opcional."
                )
              : t(
                  "video.startCallPolicyUseHint",
                  "Use câmera obrigatória só quando ver a pessoa for necessário. Não substitui atendimento por chat, telefone ou chamada com câmera opcional."
                )}
          </Typography>
        </Alert>

        {!team && !canSendInvite ? (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1.5, lineHeight: 1.4 }}>
            {t(
              "video.startCallComposerHint",
              "O link será colocado no composer para você enviar."
            )}
          </Typography>
        ) : null}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={busy} sx={{ textTransform: "none" }}>
          {t("video.close", "Cancelar")}
        </Button>
        <Button
          variant="contained"
          disabled={busy}
          onClick={handleConfirm}
          sx={{ textTransform: "none", fontWeight: 700 }}
        >
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
